/**
 * Sizing for pdf.js canvas rendering.
 *
 * iOS Safari caps a single canvas at ~16.7M pixels and kills the tab when the
 * total canvas memory gets large, so the backing store is limited by both a
 * device-pixel-ratio cap and a per-canvas pixel budget.
 */
export const MAX_DEVICE_PIXEL_RATIO = 2;
export const MAX_CANVAS_PIXELS = 4096 * 4096;

export interface PdfRenderSize {
  /** pdf.js viewport scale for the backing store. */
  scale: number;
  /** CSS size of the page on screen. */
  cssWidth: number;
  cssHeight: number;
  /** Backing-store (canvas attribute) size in device pixels. */
  pixelWidth: number;
  pixelHeight: number;
}

/**
 * @param pageWidth  page width at scale 1 (PDF points)
 * @param pageHeight page height at scale 1 (PDF points)
 * @param availableWidth CSS pixels available for the page
 * @param devicePixelRatio window.devicePixelRatio
 */
export function computePdfRenderSize(
  pageWidth: number,
  pageHeight: number,
  availableWidth: number,
  devicePixelRatio: number
): PdfRenderSize {
  const safePageWidth = pageWidth > 0 ? pageWidth : 612;
  const safePageHeight = pageHeight > 0 ? pageHeight : 792;
  const width = availableWidth > 0 ? availableWidth : safePageWidth;
  const cssScale = width / safePageWidth;
  const cssWidth = Math.floor(safePageWidth * cssScale);
  const cssHeight = Math.floor(safePageHeight * cssScale);

  let ratio = devicePixelRatio > 0 ? Math.min(devicePixelRatio, MAX_DEVICE_PIXEL_RATIO) : 1;
  const area = cssWidth * cssHeight * ratio * ratio;
  if (area > MAX_CANVAS_PIXELS) {
    ratio = Math.sqrt(MAX_CANVAS_PIXELS / (cssWidth * cssHeight));
  }
  const scale = cssScale * ratio;
  return {
    scale,
    cssWidth,
    cssHeight,
    pixelWidth: Math.floor(safePageWidth * scale),
    pixelHeight: Math.floor(safePageHeight * scale)
  };
}
